import React, { useState, useEffect, useCallback } from "react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "./ui/dialog";
import { Input } from "./ui/input";
import { Textarea } from "./ui/textarea";
import { Button } from "./ui/button";
import { Label } from "./ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "./ui/select";
import { Calendar } from "./ui/calendar";
import { Popover, PopoverContent, PopoverTrigger } from "./ui/popover";
import { CalendarIcon, Sparkles } from "lucide-react";
import { format } from "date-fns";
import { toast } from "sonner";
import BookingSuccess from "./BookingSuccess";
import { TIME_SLOTS, validateBookingForm, submitBooking } from "../lib/booking";

const INITIAL = {
  name: "",
  phone: "",
  email: "",
  service_id: "",
  date: null,
  time: "",
  notes: "",
};

const INPUT_CLS =
  "bg-[#141414] border-white/10 text-white focus:border-[#FF2079] focus-visible:ring-0 rounded-sm h-11";
const LABEL_CLS = "text-[10px] tracking-[0.25em] uppercase text-[#A09CA3]";

export default function BookingForm({ open, onClose, services, preselectedService }) {
  const [form, setForm] = useState(INITIAL);
  const [submitting, setSubmitting] = useState(false);
  const [success, setSuccess] = useState(null);

  useEffect(() => {
    if (preselectedService) {
      setForm((f) => ({ ...f, service_id: preselectedService.id }));
    }
  }, [preselectedService]);

  useEffect(() => {
    if (open) return undefined;
    const t = setTimeout(() => {
      setForm(INITIAL);
      setSuccess(null);
    }, 250);
    return () => clearTimeout(t);
  }, [open]);

  const handleChange = useCallback((key, value) => {
    setForm((f) => ({ ...f, [key]: value }));
  }, []);

  const handleSubmit = async (e) => {
    e.preventDefault();
    const validationError = validateBookingForm(form);
    if (validationError) {
      toast.error(validationError);
      return;
    }
    setSubmitting(true);
    const result = await submitBooking(form, services);
    setSubmitting(false);
    if (result.ok) {
      setSuccess(result.booking);
      toast.success("Reservation received. We will confirm shortly.");
    } else {
      toast.error(result.error);
    }
  };

  return (
    <Dialog open={open} onOpenChange={(o) => !o && onClose()}>
      <DialogContent
        data-testid="booking-dialog"
        className="bg-[#0E0E0E] border border-white/10 text-white max-w-xl max-h-[92vh] overflow-y-auto"
      >
        <DialogHeader>
          <div className="eyebrow mb-2">Reservation</div>
          <DialogTitle className="font-display text-3xl text-white font-light">
            {success ? "You're booked." : "Reserve your ritual"}
          </DialogTitle>
          <DialogDescription className="text-[#A09CA3]">
            {success
              ? "Our concierge will reach out shortly to confirm your appointment."
              : "Tell us your details and we'll prepare the studio for you."}
          </DialogDescription>
        </DialogHeader>

        {success ? (
          <BookingSuccess booking={success} onClose={onClose} />
        ) : (
          <form onSubmit={handleSubmit} className="space-y-4 pt-2">
            <div className="grid grid-cols-2 gap-3">
              <div className="space-y-1.5">
                <Label className={LABEL_CLS}>Name</Label>
                <Input
                  data-testid="booking-name-input"
                  value={form.name}
                  onChange={(e) => handleChange("name", e.target.value)}
                  required
                  className={INPUT_CLS}
                />
              </div>
              <div className="space-y-1.5">
                <Label className={LABEL_CLS}>Phone</Label>
                <Input
                  data-testid="booking-phone-input"
                  value={form.phone}
                  onChange={(e) => handleChange("phone", e.target.value)}
                  required
                  className={INPUT_CLS}
                />
              </div>
            </div>

            <div className="space-y-1.5">
              <Label className={LABEL_CLS}>Email (optional)</Label>
              <Input
                data-testid="booking-email-input"
                type="email"
                value={form.email}
                onChange={(e) => handleChange("email", e.target.value)}
                className={INPUT_CLS}
              />
            </div>

            <div className="space-y-1.5">
              <Label className={LABEL_CLS}>Service</Label>
              <Select value={form.service_id} onValueChange={(v) => handleChange("service_id", v)}>
                <SelectTrigger
                  data-testid="booking-service-select"
                  className="bg-[#141414] border-white/10 text-white rounded-sm h-11 focus:ring-0 focus:border-[#FF2079]"
                >
                  <SelectValue placeholder="Choose a service" />
                </SelectTrigger>
                <SelectContent className="bg-[#141414] border-white/10 text-white max-h-[300px]">
                  {services.map((s) => (
                    <SelectItem key={s.id} value={s.id} className="focus:bg-[#FF2079]/20 focus:text-white">
                      <span className="text-[#FFB6C1] mr-2">{s.category}</span>
                      {s.name} — {s.price_label || `$${s.price}`}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            <div className="grid grid-cols-2 gap-3">
              <div className="space-y-1.5">
                <Label className={LABEL_CLS}>Date</Label>
                <Popover>
                  <PopoverTrigger asChild>
                    <Button
                      type="button"
                      variant="outline"
                      data-testid="booking-date-btn"
                      className="w-full justify-start bg-[#141414] border-white/10 text-white hover:bg-[#1a1a1a] hover:text-white rounded-sm h-11 font-normal"
                    >
                      <CalendarIcon className="mr-2 w-4 h-4 text-[#FF2079]" />
                      {form.date ? format(form.date, "PPP") : <span className="text-[#A09CA3]">Pick a date</span>}
                    </Button>
                  </PopoverTrigger>
                  <PopoverContent className="w-auto p-0 bg-[#141414] border-white/10" align="start">
                    <Calendar
                      mode="single"
                      selected={form.date}
                      onSelect={(d) => handleChange("date", d)}
                      disabled={(d) => d < new Date(new Date().setHours(0, 0, 0, 0))}
                      initialFocus
                      className="bg-[#141414] text-white"
                    />
                  </PopoverContent>
                </Popover>
              </div>
              <div className="space-y-1.5">
                <Label className={LABEL_CLS}>Time</Label>
                <Select value={form.time} onValueChange={(v) => handleChange("time", v)}>
                  <SelectTrigger
                    data-testid="booking-time-select"
                    className="bg-[#141414] border-white/10 text-white rounded-sm h-11 focus:ring-0 focus:border-[#FF2079]"
                  >
                    <SelectValue placeholder="Pick a time" />
                  </SelectTrigger>
                  <SelectContent className="bg-[#141414] border-white/10 text-white max-h-[260px]">
                    {TIME_SLOTS.map((t) => (
                      <SelectItem key={t} value={t} className="focus:bg-[#FF2079]/20 focus:text-white">
                        {t}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            </div>

            <div className="space-y-1.5">
              <Label className={LABEL_CLS}>Notes (optional)</Label>
              <Textarea
                data-testid="booking-notes-input"
                value={form.notes}
                onChange={(e) => handleChange("notes", e.target.value)}
                rows={3}
                placeholder="Any preferences or requests…"
                className="bg-[#141414] border-white/10 text-white focus:border-[#FF2079] focus-visible:ring-0 rounded-sm resize-none"
              />
            </div>

            <Button
              type="submit"
              disabled={submitting}
              data-testid="booking-submit-btn"
              className="pink-glow-btn w-full bg-[#FF2079] hover:bg-[#D91666] text-white rounded-sm h-12 text-[11px] tracking-[0.28em] uppercase font-medium disabled:opacity-60"
            >
              {submitting ? (
                "Reserving…"
              ) : (
                <>
                  <Sparkles className="w-4 h-4 mr-2" />
                  Confirm Reservation
                </>
              )}
            </Button>
          </form>
        )}
      </DialogContent>
    </Dialog>
  );
}
